<script setup>
import { companyNumber, kindsFor } from '@/lib/contactPoints.js'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMeta } from '@/stores/meta.js'
import { formatDateTime } from '@/lib/format.js'
import { formatPhone, telHref } from '@/lib/phone.js'
import { FOLLOW_UPS, followUpAt, retries } from '@/lib/followUp.js'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiField from '@/components/ui/UiField.vue'
import NumberInput from '@/components/form/NumberInput.vue'
import DateTimeInput from '@/components/form/DateTimeInput.vue'
import { DictationField } from '@addictivegroup/addictive-dictation'
import { looksLikePhone } from '@/lib/callNotes.js'

/**
 * Closing an activity, as Dynamics' "Close Activity": completed with an outcome, or cancelled
 * with a reason (required), the time it actually took and a note. Both reason lists are
 * dictionaries the administrator keeps (activity_outcome, activity_cancel_reason).
 *
 * Completed, it can be set again (D-194): "call again" tomorrow, in two days, a week, a month or
 * at a time chosen. An outcome marked so in the dictionary (no answer, voicemail…) offers it
 * at once; any other has it a click away.
 *
 * After a call (`call`: the number dialled and whom): the summary of the conversation, to be
 * dictated, instead of a closing note — it becomes a note on what the call is about — and the
 * time it took, counted from leaving for the phone app to coming back (unless typed).
 *
 * A call with no number (`call.phone` empty) opens the same screen with a section above it for
 * the number (`call.numberFor`: the record it goes to, or none). The number, the summary or both
 * can be saved without closing the task ("keep") — it stays open for the call to come.
 */
const props = defineProps({
  activity: { type: Object, required: true },
  mode: { type: String, default: 'complete' }, // complete | cancel
  call: { type: Object, default: null }, // { phone, person, numberFor } — the call just made, or one with no number
})
const emit = defineEmits(['close', 'confirm', 'keep'])
const meta = useMeta()

const state = ref(props.mode === 'cancel' ? 'cancel' : 'complete')
const reason = ref('')
const note = ref('')
const busy = ref(false)

// A meeting took, unless said otherwise, the time it was booked for.
const scheduled = () => {
  const a = props.activity
  if (!a.start_at || !a.end_at) return null
  const minutes = Math.round((new Date(a.end_at) - new Date(a.start_at)) / 60000)
  return minutes > 0 && minutes <= 10080 ? minutes : null
}
const minutes = ref(props.activity.actual_duration_minutes ?? scheduled())
const minutesTyped = ref(false)

// A call with no number: one to add, where it belongs.
const missingNumber = computed(() => !!props.call && !props.call.phone)
const newPhone = ref('')
// The kinds a number may be added as (D-229): those of the dictionary, a company's main one first for an account.
const phoneKinds = computed(() => kindsFor(meta, 'phone').filter((k) => k.key !== 'fax'))
const newKind = ref(props.call?.numberFor?.entity === 'account' ? 'main' : 'work')
const phoneValid = computed(() => !newPhone.value.trim() || looksLikePhone(newPhone.value))
const phonePayload = () => (missingNumber.value && props.call.numberFor && newPhone.value.trim() ? { value: newPhone.value.trim(), kind: newKind.value } : null)
const canKeep = computed(() => phoneValid.value && (!!phonePayload() || !!note.value.trim()))
function keep() {
  emit('keep', { phone: phonePayload(), summary: note.value.trim() || null })
}

// The call's length: from leaving for the phone app to coming back, in whole minutes (at least one).
let leftAt = null
function onVisibility() {
  if (!props.call) return
  if (document.visibilityState === 'hidden') leftAt = Date.now()
  else if (leftAt !== null && !minutesTyped.value) minutes.value = Math.max(1, Math.round((Date.now() - leftAt) / 60000))
}
onMounted(() => document.addEventListener('visibilitychange', onVisibility))
onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisibility))

const dictionary = computed(() => (state.value === 'cancel' ? 'activity_cancel_reason' : 'activity_outcome'))
// The outcomes for this kind of activity (D-293): one naming no kinds fits them all.
const entries = computed(() => (meta.dictionary(dictionary.value)?.entries || []).filter((e) => dictionary.value !== 'activity_outcome' || !e.meta?.types?.length || e.meta.types.includes(props.activity.type)))
const reasonRequired = computed(() => state.value === 'cancel')
const minutesValid = computed(() => minutes.value === null || (Number.isInteger(minutes.value) && minutes.value >= 0 && minutes.value <= 10080))
// Done again: a choice of FOLLOW_UPS, 'other' (at `otherAt`), or nothing.
const again = ref(null)
const otherAt = ref(null)
const againOpen = ref(false)
const suggested = computed(() => state.value === 'complete' && retries(entries.value, reason.value))
watch(suggested, (yes) => {
  if (yes) againOpen.value = true
})
// Over a weekend "tomorrow" and "in 2 days" are the same Monday: offered once.
const choices = computed(() => FOLLOW_UPS.map((key) => ({ key, at: followUpAt(key, props.activity) })).filter((c, i, all) => all.findIndex((o) => o.at === c.at) === i))
const againAt = computed(() => (state.value !== 'complete' || !again.value ? null : again.value === 'other' ? otherAt.value : choices.value.find((c) => c.key === again.value)?.at || null))
const againValid = computed(() => again.value !== 'other' || (!!otherAt.value && new Date(otherAt.value) > new Date()))
const kind = computed(() => (['call', 'email', 'meeting', 'task'].includes(props.activity.type) ? props.activity.type : 'task'))
const when = (iso) => {
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(new Date(iso))
  return `${weekday} ${formatDateTime(iso)}`
}

const canConfirm = computed(() => minutesValid.value && phoneValid.value && (!reasonRequired.value || reason.value) && againValid.value)

function pick(value) {
  if (state.value === value) return
  state.value = value
  reason.value = ''
}

async function confirm() {
  busy.value = true
  try {
    await emit('confirm', {
      phone: phonePayload(),
      action: state.value,
      body: {
        [state.value === 'cancel' ? 'reason' : 'outcome']: reason.value || null,
        // A call's summary is a note of its own (the caller saves it); a closing note stays with the close.
        note: props.call ? null : note.value || null,
        ...(props.call && note.value.trim() ? { summary: note.value.trim() } : {}),
        actual_duration_minutes: minutes.value,
        ...(againAt.value ? { follow_up: { at: againAt.value } } : {}),
      },
    })
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiModal open :title="call ? $t('activity.call.title') : $t('activity.closeTitle')" size="sm" @close="emit('close')">
    <div class="space-y-3" data-testid="activity-close-dialog">
      <p class="truncate text-sm text-muted">{{ activity.subject || activity.label }}</p>
      <!-- no number to dial: the number first, apart from the call -->
      <section v-if="missingNumber" class="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20" data-testid="call-number">
        <p class="text-sm font-medium"><i class="fa-solid fa-phone-slash mr-1" aria-hidden="true"></i>{{ $t('activity.callDetails.numberSection') }}</p>
        <UiField v-if="call.numberFor" :label="$t('activity.callDetails.phoneFor', { who: call.numberFor.label || '' })" :error="phoneValid ? '' : $t('activity.callDetails.phoneInvalid')">
          <div class="flex gap-2">
            <select v-if="call.numberFor.entity !== 'lead'" v-model="newKind" class="input w-auto" :aria-label="$t('activity.callDetails.kind')">
              <option v-for="k in phoneKinds" :key="k.key" :value="k.key">{{ k.label }}</option>
            </select>
            <input v-model="newPhone" type="tel" inputmode="tel" autocomplete="tel" class="input min-w-0 flex-1" placeholder="+48 600 000 000" data-testid="call-new-phone" @blur="newPhone = newPhone.trim() ? formatPhone(newPhone, call.numberFor?.country) : newPhone" />
          </div>
        </UiField>
        <p v-else class="text-sm text-muted" data-testid="call-no-target">{{ $t('activity.callDetails.noTarget') }}</p>
      </section>
      <p v-if="call" class="flex flex-wrap items-center gap-x-2 text-sm" data-testid="call-who">
        <span v-if="call.person"><i :class="['fa-solid mr-1 text-faint', call.person.entity === 'account' ? 'fa-building' : 'fa-user']" aria-hidden="true"></i>{{ call.person.label }}</span>
        <a v-if="call.phone" :href="telHref(call.phone)" class="link"><i class="fa-solid fa-phone mr-1" aria-hidden="true"></i>{{ formatPhone(call.phone) }}</a>
        <!-- the company's number, not a person's (D-250) -->
        <span v-if="companyNumber(call.person, meta, $t)" class="text-xs text-muted" data-testid="call-company-number">({{ companyNumber(call.person, meta, $t) }})</span>
      </p>
      <UiField v-if="call" :label="$t('activity.call.summary')">
        <DictationField multiline @update="(text) => (note = text)">
          <textarea v-model="note" class="input min-h-24" rows="4" maxlength="20000" :placeholder="$t('activity.call.summaryHint')" data-testid="call-summary"></textarea>
        </DictationField>
      </UiField>
      <div class="grid grid-cols-2 gap-2" role="radiogroup" :aria-label="$t('activity.closeAs')">
        <button v-for="s in ['complete', 'cancel']" :key="s" type="button" role="radio" :aria-checked="state === s" :data-testid="`close-as-${s}`"
          :class="['flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium', state === s ? (s === 'cancel' ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200' : 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200') : 'border-default text-muted hover:bg-[var(--surface-3)]']"
          @click="pick(s)">
          <i :class="['fa-solid', s === 'cancel' ? 'fa-ban' : 'fa-check']" aria-hidden="true"></i>{{ $t(s === 'cancel' ? 'activity.closedCancelled' : 'activity.closedCompleted') }}
        </button>
      </div>
      <UiField :label="state === 'cancel' ? $t('activity.cancelReason') : $t('activity.outcome')" :required="reasonRequired">
        <select v-model="reason" class="input" data-testid="close-reason">
          <option value="">{{ $t('common.select') }}</option>
          <option v-for="e in entries" :key="e.key" :value="e.key">{{ meta.localized(e.label) }}</option>
        </select>
      </UiField>
      <div v-if="state === 'complete'" :class="['rounded-lg border p-3', suggested ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20' : 'border-default']" data-testid="close-again">
        <button v-if="!againOpen" type="button" class="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300" data-testid="close-again-open" @click="againOpen = true">
          <i class="fa-solid fa-rotate-right mr-1" aria-hidden="true"></i>{{ $t(`activity.again.${kind}`) }}…
        </button>
        <template v-else>
          <p class="mb-2 text-sm font-medium"><i class="fa-solid fa-rotate-right mr-1" aria-hidden="true"></i>{{ $t(`activity.again.${kind}`) }}</p>
          <div class="flex flex-wrap gap-1.5" role="radiogroup" :aria-label="$t(`activity.again.${kind}`)">
            <button v-for="c in [...choices, { key: 'other' }]" :key="c.key" type="button" role="radio" :aria-checked="again === c.key" :data-testid="`again-${c.key}`"
              :class="['flex flex-col items-start rounded-lg border px-2.5 py-1 text-left text-sm', again === c.key ? 'border-brand-500 bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-100' : 'border-default hover:bg-[var(--surface-3)]']"
              @click="again = again === c.key ? null : c.key">
              <span>{{ $t(`activity.again.${c.key}`) }}</span>
              <span v-if="c.at" class="text-2xs font-normal text-muted">{{ when(c.at) }}</span>
            </button>
          </div>
          <div v-if="again === 'other'" class="mt-2"><DateTimeInput v-model="otherAt" :invalid="!againValid" data-testid="again-other-at" /></div>
          <p v-if="againAt" class="mt-2 text-xs text-muted">{{ $t('activity.again.hint') }}</p>
        </template>
      </div>
      <UiField :label="$t('activity.actualDuration')" :error="minutesValid ? '' : $t('activity.durationInvalid')">
        <div class="flex items-center gap-2">
          <NumberInput :model-value="minutes" integer :min="0" :max="10080" class="w-28" data-testid="close-minutes" @update:model-value="(v) => { minutes = v; minutesTyped = true }" />
          <span class="text-sm text-muted">{{ $t('activity.minutes') }}</span>
        </div>
      </UiField>
      <UiField v-if="!call" :label="$t('activity.note')"><textarea v-model="note" class="input" rows="3" maxlength="2000" data-testid="close-note"></textarea></UiField>
    </div>
    <template #footer>
      <UiButton @click="emit('close')">{{ $t('common.cancel') }}</UiButton>
      <UiButton v-if="missingNumber" :disabled="!canKeep" :title="$t('activity.callDetails.keepHint')" data-testid="call-keep" @click="keep">{{ $t('activity.callDetails.keep') }}</UiButton>
      <UiButton :variant="state === 'cancel' ? 'danger' : 'success'" :loading="busy" :disabled="!canConfirm" data-testid="close-confirm" @click="confirm">
        {{ $t(state === 'cancel' ? 'activity.cancelActivity' : againAt ? 'activity.again.confirm' : 'actions.complete') }}
      </UiButton>
    </template>
  </UiModal>
</template>
